import {Component, OnInit} from '@angular/core';
import {UtilService} from '../../../services/util/util.service';
import {ViewService} from '../../../services/view/view.service';
import {FeeService} from '../../../services/fee/fee.service';
import {finalize} from 'rxjs/operators';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {DeliveryTypeDialogComponent} from "../delivery-type-dialog/delivery-type-dialog.component";
import {DeliveryTypeWithFee, FeePeriodPrice} from "../../../generated/sales-terminal-client";
import {TranslateService} from "@ngx-translate/core";
import {SortedFee} from "../service-fees/service-fees.component";
import {SnackService} from '../../../services/snack/snack.service';
import {DeliveryTypeService} from '../../../services/delivery-type/delivery-type.service';

export interface SortedDeliveryTypeWithFee extends DeliveryTypeWithFee {
  sortedFee?: SortedFee;
}

@Component({
  selector: 'app-delivery-types',
  templateUrl: './delivery-types.component.html',
  styleUrls: ['./delivery-types.component.scss']
})
export class DeliveryTypesComponent implements OnInit {
  public isLoading: boolean;
  public deliveryTypeIds: number[] = [];
  public sortedDeliveryTypes: Array<SortedDeliveryTypeWithFee>;
  public expanded: Record<number, boolean> = {};
  public activeTab: number = 0;
  public activeExpandableCount: number;
  public pastExpandableCount: number;

  constructor(public utilService: UtilService,
              public viewService: ViewService,
              private dialog: MatDialog,
              private deliveryService: DeliveryTypeService,
              private feeService: FeeService,
              private translateService: TranslateService,
              private snackService: SnackService) {
  }

  public ngOnInit(): void {
    this.search();
  }

  public search(expired = false): void {
    this.isLoading = true;
    this.deliveryService.getDeliveryTypes(expired).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((results: Array<DeliveryTypeWithFee>) => {
      this.expanded = {};
      this.deliveryTypeIds = results.map(dt => dt.id);
      this.sortDeliveryTypes(results);
    });
  }
  private sortDeliveryTypes(deliveryTypes: Array<DeliveryTypeWithFee>): void {
    this.sortedDeliveryTypes = this.filterResultsAndSortPeriodPrices(deliveryTypes);
    this.activeExpandableCount = this.sortedDeliveryTypes.filter((deliveryType: SortedDeliveryTypeWithFee) => deliveryType.sortedFee.currentAndFuturePeriodPrices?.length > 1).length;
    this.pastExpandableCount = this.sortedDeliveryTypes.filter((deliveryType: SortedDeliveryTypeWithFee) => deliveryType.sortedFee.pastPeriodPrices?.length > 1).length;
  }

  public expandAll(): void {
    const data: Record<number, boolean> = {};
    this.sortedDeliveryTypes.forEach((deliveryType: SortedDeliveryTypeWithFee) => {
      const expandablePeriodPrices = this.getRelevantPeriodPrices(deliveryType);
      if (expandablePeriodPrices?.length > 1) {
        data[deliveryType.id] = true;
      }
    });
    this.expanded = data;
  }
  public collapseAll(): void {
    this.expanded = {};
  }
  public get areAllExpanded(): boolean {
    return Object.values(this.expanded).length === (this.activeTab === 0 ? this.activeExpandableCount : this.pastExpandableCount) && Object.values(this.expanded).every(expanded => !!expanded);
  }

  public createDeliveryType(): void {
    const dialogRef: MatDialogRef<DeliveryTypeDialogComponent> = this.dialog.open(DeliveryTypeDialogComponent, {
      width: '60%'
    });
    dialogRef.afterClosed().subscribe(_ => this.search(this.activeTab === 1));
  }
  public editDeliveryType(deliveryType: SortedDeliveryTypeWithFee): void {
    const dialogRef: MatDialogRef<DeliveryTypeDialogComponent> = this.dialog.open(DeliveryTypeDialogComponent, {
      width: '60%',
      data: deliveryType
    });
    dialogRef.afterClosed().subscribe(_ => this.search(this.activeTab === 1));
  }

  public changeTab(): void {
    this.search(this.activeTab === 1);
  }
  public toggleDeliveryType(deliveryTypeId: number): void {
    this.expanded[deliveryTypeId] = !this.expanded[deliveryTypeId];
  }

  public getDeliveryTypeChannels(deliveryType: DeliveryTypeWithFee): string {
    return deliveryType.salesChannels.map(sc => this.translateService.instant("general.api_delivery_types_table_sales_channel_" + sc.toLowerCase())).join(", ");
  }

  public updateOrder(deliveryType: DeliveryTypeWithFee, direction: number): void {
    const deliveryTypeIndex: number = this.deliveryTypeIds.indexOf(deliveryType.id);
    [this.deliveryTypeIds[deliveryTypeIndex], this.deliveryTypeIds[deliveryTypeIndex + direction]] = [this.deliveryTypeIds[deliveryTypeIndex + direction], this.deliveryTypeIds[deliveryTypeIndex]]
    this.deliveryService.updateDeliveryTypesOrder(this.deliveryTypeIds).subscribe((results: Array<DeliveryTypeWithFee>) => {
      const returnedOrder: number[] = results.map(dt => dt.id);
      if (this.listsDiffer(this.deliveryTypeIds, returnedOrder)) {
        this.snackService.showError(this.translateService.instant('general.api_delivery_types_order_error'));
        this.deliveryTypeIds = returnedOrder;
      }
      this.sortDeliveryTypes(results);
    });
  }

  private listsDiffer(list1: number[], list2: number[]): boolean {
    return JSON.stringify(list1) !== JSON.stringify(list2);
  }

  getRowSpan(deliveryType: DeliveryTypeWithFee): number {
    return this.expanded[deliveryType.id] ? (this.getRelevantPeriodPrices(deliveryType)?.length || 1) : 1;
  }

  private filterResultsAndSortPeriodPrices(deliveryTypes: Array<SortedDeliveryTypeWithFee>): SortedDeliveryTypeWithFee[] {
    deliveryTypes.forEach(dt => dt.sortedFee = this.feeService.sortServiceFeePeriods(dt.fee));
    return deliveryTypes;
  }

  getRelevantPeriodPrices(deliveryType: SortedDeliveryTypeWithFee): FeePeriodPrice[] {
    return this.activeTab === 0 ? deliveryType.sortedFee?.currentAndFuturePeriodPrices : deliveryType.sortedFee?.pastPeriodPrices;
  }
}
